import React from "react";
import { Button, Tooltip } from "antd";
import { ReloadOutlined, FileTextOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import { formatDistanceToNow, format, isValid } from "date-fns";
import { askAiTokens as t } from "./tokens";
import { fileIconFor, formatFileSize } from "./fileUtils";

interface DocItem {
  doc_id: string;
  filename?: string;
  file_size_bytes?: number;
  uploaded_at?: string;
  upload_status?: string;
  blob_url?: string;
}

interface Props {
  docs: DocItem[] | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

const relativeDate = (iso?: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (!isValid(d)) return iso;
  return formatDistanceToNow(d, { addSuffix: true });
};

const absoluteDate = (iso?: string) => {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (!isValid(d)) return undefined;
  return format(d, "PPp");
};

const statusDot = (s?: string): { color: string; label: string } => {
  switch (s) {
    case "embedded":
    case "ready":
      return { color: t.color.success, label: "Ready" };
    case "pending":
      return { color: t.color.warning, label: "Pending" };
    case "chunking":
    case "embedding":
      return { color: t.color.accent, label: "Indexing" };
    case "failed":
    case "error":
      return { color: t.color.danger, label: "Failed" };
    default:
      return { color: t.color.textSubtle, label: s || "" };
  }
};

const DocRow: React.FC<{ doc: DocItem; index: number }> = ({ doc, index }) => {
  const status = statusDot(doc.upload_status);
  const dateTip = absoluteDate(doc.uploaded_at);
  const clickable = Boolean(doc.blob_url);
  return (
    <motion.a
      href={clickable ? doc.blob_url : undefined}
      target={clickable ? "_blank" : undefined}
      rel={clickable ? "noopener noreferrer" : undefined}
      initial={{ opacity: 0, x: -4 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{
        duration: 0.22,
        delay: Math.min(index * 0.02, 0.2),
        ease: [0.22, 1, 0.36, 1],
      }}
      className="askai-doc-row"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "10px 12px",
        borderRadius: 8,
        cursor: clickable ? "pointer" : "default",
        textDecoration: "none",
        color: "inherit",
        marginBottom: 2,
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          flexShrink: 0,
          borderRadius: 8,
          background: t.color.surfaceMuted,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 18,
        }}
      >
        {fileIconFor(doc.filename || "")}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: t.color.text,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            letterSpacing: -0.1,
          }}
          title={doc.filename}
        >
          {doc.filename || "Untitled document"}
        </div>
        <Tooltip title={dateTip} mouseEnterDelay={0.4}>
          <div
            style={{
              fontSize: 11.5,
              color: t.color.textSubtle,
              marginTop: 2,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {doc.file_size_bytes != null && (
              <>
                <span>{formatFileSize(doc.file_size_bytes)}</span>
                <span style={{ opacity: 0.5 }}>·</span>
              </>
            )}
            <span>{relativeDate(doc.uploaded_at)}</span>
            {doc.upload_status && (
              <>
                <span style={{ opacity: 0.5 }}>·</span>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    color: status.color,
                    fontWeight: 500,
                  }}
                >
                  <span
                    style={{
                      width: 5,
                      height: 5,
                      borderRadius: "50%",
                      background: status.color,
                    }}
                  />
                  {status.label}
                </span>
              </>
            )}
          </div>
        </Tooltip>
      </div>
    </motion.a>
  );
};

const DocumentsPanel: React.FC<Props> = ({
  docs,
  loading,
  error,
  onRefresh,
}) => {
  const totalCount = docs?.length ?? 0;
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 14px 10px",
          borderBottom: `1px solid ${t.color.border}`,
        }}
      >
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: t.color.text,
              letterSpacing: -0.1,
            }}
          >
            Documents
          </span>
          {totalCount > 0 && (
            <span
              style={{
                fontSize: 11,
                color: t.color.textSubtle,
                marginTop: 1,
              }}
            >
              {totalCount} file{totalCount === 1 ? "" : "s"} indexed
            </span>
          )}
        </div>
        <Tooltip title="Refresh">
          <Button
            type="text"
            size="small"
            loading={loading}
            onClick={onRefresh}
            icon={<ReloadOutlined />}
          />
        </Tooltip>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "4px 6px 10px" }}>
        {loading && docs === null ? (
          <DocsEmpty>Loading documents…</DocsEmpty>
        ) : error ? (
          <DocsEmpty tone="danger">{error}</DocsEmpty>
        ) : totalCount === 0 ? (
          <DocsEmpty icon>
            No documents uploaded for this tender yet.
            <div style={{ fontSize: 11.5, marginTop: 4 }}>
              Drop files in chat to index them.
            </div>
          </DocsEmpty>
        ) : (
          docs!.map((d, i) => <DocRow key={d.doc_id} doc={d} index={i} />)
        )}
      </div>
    </div>
  );
};

const DocsEmpty: React.FC<{
  children: React.ReactNode;
  tone?: "danger";
  icon?: boolean;
}> = ({ children, tone, icon }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 10,
      padding: "44px 18px",
      textAlign: "center",
      color: tone === "danger" ? t.color.danger : t.color.textSubtle,
      fontSize: 12.5,
      lineHeight: 1.55,
    }}
  >
    {icon && (
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          background: t.color.surfaceMuted,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: t.color.textMuted,
          fontSize: 18,
        }}
      >
        <FileTextOutlined />
      </div>
    )}
    <div>{children}</div>
  </div>
);

export default DocumentsPanel;
